import {
    UseMutationOptions,
    UseMutationResult,
    useMutation,
    useQueryClient,
} from "react-query";

import { ICreateOrderResponse } from "../../types";
import { api } from "../../utils";
import { useAuth } from "../useAuth";
import { useMessage } from "../useMessage";

export interface IResendPaymentLinkParameters {
    orderId: string;
}

/**
 * Hook for resending payment links.
 *
 * @param invalidQueryKeys - Keys to invalidate after successful mutation
 * @param options - Custom mutation options
 * @returns Mutation result for resending payment link
 */
export const useResendPaymentLink = (
    invalidQueryKeys?: string | string[],
    options?: UseMutationOptions<
        ICreateOrderResponse,
        unknown,
        IResendPaymentLinkParameters,
        unknown
    >,
): UseMutationResult<
    ICreateOrderResponse,
    unknown,
    IResendPaymentLinkParameters
> => {
    const auth = useAuth();
    const queryClient = useQueryClient();
    const message = useMessage();

    return useMutation<
        ICreateOrderResponse,
        unknown,
        IResendPaymentLinkParameters
    >({
        ...options,
        mutationFn: (
            parameters: IResendPaymentLinkParameters,
        ): Promise<ICreateOrderResponse> => {
            if (!auth.token) {
                throw new Error(
                    "Cannot resend payment link without a valid token.",
                );
            }

            return api.v2.Orders.resendPaymentLink({
                ...parameters,
                token: auth.token,
            });
        },
        onError: (error: any) => message.showError(error),
        onSuccess: (
            order: ICreateOrderResponse,
            variables: IResendPaymentLinkParameters,
            context: unknown,
        ) => {
            if (invalidQueryKeys !== undefined) {
                queryClient.invalidateQueries(invalidQueryKeys);
            }

            options?.onSuccess?.(order, variables, context);
        },
    });
};
